import {
  createRoute,
  Link,
  redirect,
  useNavigate,
} from '@tanstack/react-router';
import { T, useGT } from 'gt-react';
import { useState, type FormEvent } from 'react';
import z from 'zod';

import { useResetPassword } from 'src/api/auth/useResetPassword';
import { Button } from 'src/components/ui/button';
import { Spinner } from 'src/components/ui/spinner';
import { Route as rootRoute } from 'src/pages/__root';

import { AuthErrorMessage } from './AuthErrorMessage';
import { AuthShell } from './AuthShell';

const MIN_PASSWORD_LENGTH = 6;

export const Route = createRoute({
  path: '/reset-password',
  getParentRoute: () => rootRoute,
  validateSearch: z.object({
    token: z.string().optional(),
  }),
  beforeLoad: ({ context }) => {
    if (context.auth.currentUser) {
      throw redirect({ to: '/' });
    }
  },
  component: ResetPasswordPage,
});

export function ResetPasswordPage() {
  const gt = useGT();
  const navigate = useNavigate();
  const { token } = Route.useSearch();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState({ password: '', confirm: '' });

  const { mutate, isPending, error } = useResetPassword();
  const isLoading = isPending;

  if (!token) {
    return (
      <AuthShell
        washOut={false}
        title={
          <T context="error heading shown on the reset-password page when no token is in the URL">
            Reset link missing
          </T>
        }
        description={
          <T context="error body shown on the reset-password page when no token is in the URL">
            This reset link is incomplete. Please request a new one.
          </T>
        }
      >
        <Link
          to="/forgot-password"
          className="block text-center text-sm font-medium text-[#1a1a1a] hover:underline mt-2"
        >
          <T context="link to request a new password reset email">
            Request a new reset link
          </T>
        </Link>
      </AuthShell>
    );
  }

  // Narrow once so the handler closure sees a non-undefined token.
  const validToken: string = token;

  function validateInputs() {
    const next = { password: '', confirm: '' };
    let ok = true;

    if (password.length < MIN_PASSWORD_LENGTH) {
      next.password = gt('Password must be at least 6 characters.', {
        context:
          'validation error shown when the new password is too short on the reset-password form',
      });
      ok = false;
    }
    if (password !== confirm) {
      next.confirm = gt("Passwords don't match.", {
        context:
          'validation error shown when the confirm password does not match on the reset-password form',
      });
      ok = false;
    }

    setErrors(next);
    return ok;
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!validateInputs()) return;

    mutate(
      { token: validToken, newPassword: password },
      {
        onSuccess: () => {
          navigate({ to: '/login' });
        },
      },
    );
  }

  return (
    <AuthShell
      washOut={false}
      title={
        <T context="heading on the reset-password form">
          Choose a new password
        </T>
      }
      description={
        <T context="description on the reset-password form">
          Pick a password you haven&rsquo;t used before.
        </T>
      }
    >
      <form noValidate onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label
              htmlFor="reset-password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {gt('New password', {
                context:
                  'label for new password input on the reset-password page',
              })}
            </label>
            <input
              id="reset-password"
              type="password"
              value={password}
              disabled={isLoading}
              autoFocus
              autoComplete="new-password"
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a1a1a] focus:border-transparent outline-none transition-all disabled:bg-gray-50 disabled:text-gray-400"
            />
            {errors.password && (
              <p className="mt-1 text-sm text-red-600">{errors.password}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="reset-password-confirm"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {gt('Confirm new password', {
                context:
                  'label for confirm-password input on the reset-password page',
              })}
            </label>
            <input
              id="reset-password-confirm"
              type="password"
              value={confirm}
              disabled={isLoading}
              autoComplete="new-password"
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a1a1a] focus:border-transparent outline-none transition-all disabled:bg-gray-50 disabled:text-gray-400"
            />
            {errors.confirm && (
              <p className="mt-1 text-sm text-red-600">{errors.confirm}</p>
            )}
          </div>
        </div>

        <Button type="submit" disabled={isLoading} className="w-full mt-6">
          {isLoading ? (
            <Spinner className="mx-auto text-white" />
          ) : (
            <T context="button label to submit the reset-password form">
              Set new password
            </T>
          )}
        </Button>

        <AuthErrorMessage error={error} className="mt-4" />

        <Link
          to="/forgot-password"
          className="block text-center text-sm text-gray-500 hover:text-[#1a1a1a] mt-4"
        >
          <T context="link to request a new password reset email from the reset-password page">
            Request a new reset link
          </T>
        </Link>
      </form>
    </AuthShell>
  );
}
